import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IUseSetGroupEditTable,
  SetGroupEditTableMode,
} from '../../../../components/reusable/set-group-edit-table/interfaces';
import { useDefaultSetItemRestSec } from '../../../../hooks/use-default-set-item-rest-sec';
import { useExerciseResultsForSetAutofill } from '../../../../hooks/use-exercise-results-for-set-autofill';
import { useUserSetPreferences } from '../../../../hooks/use-user-set-preferences';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { ExerciseOptionType } from '../../../../shared/constants';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import {
  getEmptyTrainingSet,
  orderSets,
} from '../../../../utils/training/set-utils';
import { getExerciseResultsUserIdForTrainingPerformer } from '../../shared/training-owner-form';

export interface IUseEditTrainingSetTableOptions {
  trainingOwnerId?: string;
  showSetItemStatus?: boolean;
  trainingOptionValueField?: 'programValue' | 'value';
  trainingOptionValueField2?: 'programValue2' | 'value2';
  copyLastTrainingSetItemStatusOnAdd?: boolean;
}

function useEditTrainingSetTable(
  setLocalSets: React.Dispatch<React.SetStateAction<ITrainingSet[]>>,
  options?: IUseEditTrainingSetTableOptions,
): IUseSetGroupEditTable<ITrainingSet> {
  const { t } = useTranslation();
  const { data: user } = useGetMyDetails();
  const exerciseResultsUserId = useMemo(
    () =>
      getExerciseResultsUserIdForTrainingPerformer(
        options?.trainingOwnerId,
        user?.id,
      ),
    [options?.trainingOwnerId, user?.id],
  );
  const exerciseResults = useExerciseResultsForSetAutofill(
    exerciseResultsUserId,
  );
  const { showSaveSetButton, showUploadSetButton } = useUserSetPreferences();
  const { defaultRestForNewEmptyItem } = useDefaultSetItemRestSec();

  const onChangeSet = useCallback(
    (set: ITrainingSet) => {
      setLocalSets((prev) => {
        const updatedSets = prev.map((existingSet) => {
          if (existingSet.orderNumber === set.orderNumber) {
            return { ...existingSet, ...set };
          }
          return existingSet;
        });
        return updatedSets;
      });
    },
    [setLocalSets],
  );

  const onAddSet = useCallback(
    (prevSets: ITrainingSet[], newSet?: ITrainingSet) => {
      let currentSets = [...prevSets];

      if (newSet) {
        currentSets = [...currentSets, newSet];
      } else {
        const currentSet = prevSets[0];
        if (currentSet) {
          const maxOrderNumber = Math.max(
            ...prevSets.map((set) => set.orderNumber),
          );

          currentSets = [
            ...currentSets,
            getEmptyTrainingSet(
              currentSet.setNumber,
              maxOrderNumber + 1,
              currentSet.trainingId,
              defaultRestForNewEmptyItem,
            ),
          ];
        }
      }

      return orderSets(currentSets);
    },
    [defaultRestForNewEmptyItem],
  );

  const onRemoveSetsBySetNumber = useCallback(
    (prevSets: ITrainingSet[], setNumber: number, orderNumber?: number) => {
      let currentSets = [...prevSets];
      const setsCount = currentSets.filter(
        (set) => set.setNumber === setNumber,
      ).length;

      currentSets = currentSets
        .map((set) => {
          if (set.setNumber !== setNumber) {
            return set;
          }

          if (
            (orderNumber === undefined ||
              orderNumber === null ||
              setsCount === 1) &&
            set.setNumber > setNumber
          ) {
            return {
              ...set,
              setNumber: set.setNumber - 1,
            };
          }

          if (set.orderNumber === orderNumber) {
            return null;
          }

          if (set.orderNumber > orderNumber) {
            return {
              ...set,
              orderNumber: set.orderNumber - 1,
            };
          }

          return set;
        })
        .filter(Boolean);

      return orderSets(currentSets);
    },
    [],
  );

  const onAddSetWrapper = useCallback(
    (newSet?: ITrainingSet) => {
      setLocalSets((prev) => {
        const updatedSets = onAddSet(prev, newSet);
        return updatedSets;
      });
    },
    [onAddSet, setLocalSets],
  );

  const onRemoveSetsBySetNumberWrapper = useCallback(
    (setNumber: number, orderNumber?: number) => {
      setLocalSets((prev) => {
        const updatedSets = onRemoveSetsBySetNumber(
          prev,
          setNumber,
          orderNumber,
        );

        return updatedSets;
      });
    },
    [onRemoveSetsBySetNumber, setLocalSets],
  );

  return {
    options: {
      hideSuperset: false,
      hideSetsCount: true,
      hideExpand: true,
      hideHeader: true,
      hideSavedSetUploading: !showUploadSetButton,
      removeSetMode: 'set',
      hideFrame: true,
      hideSaveSet: !showSaveSetButton,
      showSetItemStatus: !!options?.showSetItemStatus,
      trainingOptionValueField:
        options?.trainingOptionValueField || 'programValue',
      trainingOptionValueField2:
        options?.trainingOptionValueField2 || 'programValue2',
      copyLastTrainingSetItemStatusOnAdd:
        !!options?.copyLastTrainingSetItemStatusOnAdd,
    },
    mode: SetGroupEditTableMode.training,
    maxSetNumber: 0,
    submitCalled: false,
    optionValidators: [
      {
        message: t('formikValidation.minValue').replace('{0}', '0'),
        validator: (value: number) => {
          return value < 0;
        },
      },
      {
        type: ExerciseOptionType.integer,
        message: t('formikValidation.integerValueRequired'),
        validator: (value: number) => {
          return +value === value && !Number.isInteger(value);
        },
      },
    ],
    onChangeSet,
    onAddSet: onAddSetWrapper,
    onRemoveSetsBySetNumber: onRemoveSetsBySetNumberWrapper,
    onChangeSetOrder: undefined,
    exerciseResults,
  };
}

export default useEditTrainingSetTable;
